import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import BackToSiteBadge from "@/components/site/BackToSiteBadge";

const TABS = ["Overview", "Affiliates", "Payouts", "Settings", "Audit"];

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("Overview");
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/admin/me`, { withCredentials: true })
      .then((r) => setMe(r.data.user))
      .catch(() => navigate("/admin/login", { replace: true }));
  }, [navigate]);

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); } catch (_) {}
    navigate("/admin/login", { replace: true });
  };

  if (!me) return <div className="min-h-screen bg-[var(--cream)]" />;

  return (
    <div className="min-h-screen bg-[var(--cream)] text-[var(--ink)]">
      <header className="px-6 md:px-10 py-5 border-b border-[rgba(15,31,23,0.08)] flex items-center justify-between gap-3">
        <BackToSiteBadge />
        <div className="hidden md:flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[var(--ink)] text-[var(--cream)] flex items-center justify-center text-xs font-bold">N</div>
          <div>
            <div className="font-display text-lg leading-none">Nutriora Admin</div>
            <div className="text-[11px] text-[var(--muted)] mt-1 font-mono-caption tracking-[0.2em] uppercase">Milk2Income · Affiliate ops</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-[var(--muted)] hidden lg:inline">{me.email}</span>
          <Link to="/support" className="px-3 py-2 rounded-full border border-[rgba(15,31,23,0.15)] text-xs" data-testid="admin-support-link">Support</Link>
          <button onClick={logout} className="px-4 py-2 rounded-full border border-[rgba(15,31,23,0.15)] text-sm" data-testid="admin-logout">Sign out</button>
        </div>
      </header>

      <nav className="px-6 md:px-10 border-b border-[rgba(15,31,23,0.08)] overflow-x-auto">
        <div className="flex gap-6 min-w-max">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`py-4 text-sm font-medium relative ${tab === t ? "text-[var(--marigold)]" : "text-[var(--muted)]"}`}
              data-testid={`admin-tab-${t.toLowerCase()}`}
            >
              {t}
              {tab === t && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--marigold)]" />}
            </button>
          ))}
        </div>
      </nav>

      <main className="px-6 md:px-10 py-8 max-w-6xl mx-auto">
        {tab === "Overview" && <Overview />}
        {tab === "Affiliates" && <AffiliatesTab />}
        {tab === "Payouts" && <PayoutsTab />}
        {tab === "Settings" && <SettingsTab />}
        {tab === "Audit" && <AuditTab />}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/overview`, { withCredentials: true }).then((r) => setData(r.data));
  }, []);
  if (!data) return <Spin />;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl mb-4">Affiliates</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Metric label="Total" value={data.affiliates.total} />
          <Metric label="Active" value={data.affiliates.active} tone="jade" />
          <Metric label="Pending" value={data.affiliates.pending_payment} tone="marigold" />
          <Metric label="Suspended" value={data.affiliates.suspended} />
        </div>
      </div>
      <div>
        <h2 className="font-display text-2xl mb-4">Program balances</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {["pending", "available", "reserved", "paid", "reversed"].map((k) => (
            <Metric key={k} label={k} capitalise value={`$${((data.balances[k] || 0) / 100).toFixed(2)}`} />
          ))}
        </div>
      </div>
      <div>
        <h2 className="font-display text-2xl mb-4">Program health</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Metric label="Clicks (non-bot)" value={data.clicks_total} />
          <Metric label="Attributed orders" value={data.orders_attributed} />
          <Metric label="Current rate" value={`${(data.settings.commission_rate_bps / 100).toFixed(2)}%`} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Affiliates tab
// ---------------------------------------------------------------------------
function AffiliatesTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchList = async () => {
    const r = await axios.get(`${API}/admin/affiliates`, { params: { q, status }, withCredentials: true });
    setRows(r.data.items || []);
  };
  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, [q, status]);

  if (selected) return <AffiliateDetail affiliate_id={selected} onBack={() => { setSelected(null); fetchList(); }} />;

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <input className="input-base flex-1" placeholder="Search by name, email or code…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-aff-search" />
        <select className="input-base" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-aff-status">
          <option value="">All statuses</option>
          <option value="pending_payment">Pending payment</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
        </select>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase border-b border-[rgba(15,31,23,0.08)]">
            <th className="py-3 pr-3">Code</th>
            <th className="py-3 pr-3">Name</th>
            <th className="py-3 pr-3">Email</th>
            <th className="py-3 pr-3">Status</th>
            <th className="py-3 pr-3 text-right">Available</th>
            <th className="py-3 pr-3 text-right">Pending</th>
          </tr>
        </thead>
        <tbody data-testid="admin-aff-rows">
          {rows.length === 0 ? (
            <tr><td colSpan={6} className="py-8 text-center text-[var(--muted)]">No affiliates yet.</td></tr>
          ) : rows.map((a) => (
            <tr key={a.affiliate_id} className="border-b border-[rgba(15,31,23,0.05)] cursor-pointer hover:bg-white/40" onClick={() => setSelected(a.affiliate_id)} data-testid={`admin-aff-row-${a.affiliate_id}`}>
              <td className="py-3 pr-3 font-mono">{a.affiliate_id}</td>
              <td className="py-3 pr-3">{a.name}</td>
              <td className="py-3 pr-3">{a.email}</td>
              <td className="py-3 pr-3 capitalize">{a.status.replace("_", " ")}</td>
              <td className="py-3 pr-3 text-right font-mono">${((a.balances?.available || 0) / 100).toFixed(2)}</td>
              <td className="py-3 pr-3 text-right font-mono">${((a.balances?.pending || 0) / 100).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AffiliateDetail({ affiliate_id, onBack }) {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [adj, setAdj] = useState({ amount_cents: 0, reason: "" });
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    const r = await axios.get(`${API}/admin/affiliate/${affiliate_id}`, { withCredentials: true });
    setData(r.data);
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [affiliate_id]);

  const act = async (path) => {
    if (!reason.trim()) { alert("Please enter a reason first"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/affiliate/${affiliate_id}/${path}`, { reason }, { withCredentials: true });
      setReason("");
      fetchData();
    } finally { setBusy(false); }
  };

  const createAdjustment = async () => {
    if (!adj.reason.trim() || !adj.amount_cents) { alert("Amount and reason required"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/adjustment`, { affiliate_id, ...adj }, { withCredentials: true });
      setAdj({ amount_cents: 0, reason: "" });
      fetchData();
    } finally { setBusy(false); }
  };

  if (!data) return <Spin />;
  const a = data.affiliate;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-[var(--muted)] mb-4" data-testid="admin-aff-back">← Back to affiliates</button>
      <h2 className="font-display text-2xl mb-1">{a.name}</h2>
      <p className="text-[var(--muted)] mb-6">{a.email} · <span className="font-mono">{a.affiliate_id}</span> · <span className="capitalize">{a.status.replace("_", " ")}</span></p>

      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="p-4 rounded-xl bg-white/60 border border-[rgba(15,31,23,0.08)]">
          <h3 className="font-display text-lg mb-3">Balances</h3>
          <ul className="text-sm space-y-1">
            {Object.entries(data.balances).map(([k, v]) => (
              <li key={k} className="flex justify-between"><span className="capitalize text-[var(--muted)]">{k.replace("_", " ")}</span><span className="font-mono">${(v / 100).toFixed(2)}</span></li>
            ))}
          </ul>
          <div className="text-xs text-[var(--muted)] mt-3">Total clicks: {data.clicks_total}</div>
        </div>
        <div className="p-4 rounded-xl bg-white/60 border border-[rgba(15,31,23,0.08)]">
          <h3 className="font-display text-lg mb-3">Account actions</h3>
          <textarea className="input-base w-full mb-3" placeholder="Reason (required)" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="admin-reason" />
          <div className="flex flex-wrap gap-2">
            {a.status !== "active" && <button disabled={busy} onClick={() => act("activate")} className="px-4 py-2 rounded-full bg-[var(--jade)] text-white text-sm" data-testid="admin-activate">Force activate</button>}
            {a.status === "active" && <button disabled={busy} onClick={() => act("suspend")} className="px-4 py-2 rounded-full bg-red-600 text-white text-sm" data-testid="admin-suspend">Suspend</button>}
            {a.status === "suspended" && <button disabled={busy} onClick={() => act("reinstate")} className="px-4 py-2 rounded-full bg-[var(--marigold)] text-white text-sm" data-testid="admin-reinstate">Reinstate</button>}
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl bg-white/60 border border-[rgba(15,31,23,0.08)] mb-8">
        <h3 className="font-display text-lg mb-3">Manual adjustment</h3>
        <div className="grid md:grid-cols-3 gap-3">
          <input type="number" step="1" className="input-base" placeholder="Amount in cents (+ or −)" value={adj.amount_cents || ""} onChange={(e) => setAdj({ ...adj, amount_cents: parseInt(e.target.value || "0", 10) })} data-testid="admin-adj-amount" />
          <input className="input-base md:col-span-2" placeholder="Reason (required)" value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} data-testid="admin-adj-reason" />
        </div>
        <button disabled={busy} onClick={createAdjustment} className="mt-3 px-5 py-2 rounded-full bg-[var(--ink)] text-[var(--cream)] text-sm" data-testid="admin-adj-submit">Create adjustment</button>
        <p className="text-xs text-[var(--muted)] mt-2">Positive = credit; negative = debit. All adjustments are append-only and audited.</p>
      </div>

      <h3 className="font-display text-xl mb-3">Ledger (latest 100)</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase border-b border-[rgba(15,31,23,0.08)]">
              <th className="py-2 pr-3">Date</th>
              <th className="py-2 pr-3">Type</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Order</th>
              <th className="py-2 pr-3">Note</th>
              <th className="py-2 pr-3 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {data.ledger.map((e) => (
              <tr key={e.entry_id} className="border-b border-[rgba(15,31,23,0.05)]">
                <td className="py-2 pr-3 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                <td className="py-2 pr-3 capitalize">{e.type.replace("_", " ")}</td>
                <td className="py-2 pr-3 capitalize">{e.status}</td>
                <td className="py-2 pr-3 font-mono text-xs">{e.order_id || "—"}</td>
                <td className="py-2 pr-3 text-xs text-[var(--muted)]">{e.notes || ""}</td>
                <td className={`py-2 pr-3 text-right font-mono ${e.amount_cents < 0 ? "text-red-600" : ""}`}>${(e.amount_cents / 100).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payouts tab
// ---------------------------------------------------------------------------
function PayoutsTab() {
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(null);
  const [newBatch, setNewBatch] = useState({ tzs_fx_rate: "", notes: "" });
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    const r = await axios.get(`${API}/admin/payout-batches`, { withCredentials: true });
    setBatches(r.data.items || []);
  };
  useEffect(() => { refresh(); }, []);

  const createBatch = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/payout-batch`, {
        tzs_fx_rate: newBatch.tzs_fx_rate ? parseFloat(newBatch.tzs_fx_rate) : null,
        notes: newBatch.notes || null,
      }, { withCredentials: true });
      setNewBatch({ tzs_fx_rate: "", notes: "" });
      refresh();
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not create batch");
    } finally { setBusy(false); }
  };

  if (open) return <BatchDetail batch_id={open} onBack={() => { setOpen(null); refresh(); }} />;

  return (
    <div>
      <section className="p-5 rounded-xl bg-white/60 border border-[rgba(15,31,23,0.08)] mb-8">
        <h2 className="font-display text-xl mb-3">New payout batch</h2>
        <p className="text-sm text-[var(--muted)] mb-3">Sweeps every <strong>Available</strong> commission above the minimum payout threshold into a new draft batch, reserving those commissions so they can't be double-paid.</p>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <input className="input-base" placeholder="TZS FX rate (optional, 1 USD = X)" value={newBatch.tzs_fx_rate} onChange={(e) => setNewBatch({ ...newBatch, tzs_fx_rate: e.target.value })} data-testid="admin-batch-fx" />
          <input className="input-base md:col-span-2" placeholder="Notes (optional)" value={newBatch.notes} onChange={(e) => setNewBatch({ ...newBatch, notes: e.target.value })} data-testid="admin-batch-notes" />
        </div>
        <button disabled={busy} onClick={createBatch} className="px-6 py-2.5 rounded-full bg-[var(--marigold)] text-white text-sm font-medium" data-testid="admin-batch-create">Create draft batch</button>
      </section>

      <h2 className="font-display text-xl mb-3">Batches</h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase border-b border-[rgba(15,31,23,0.08)]">
            <th className="py-3 pr-3">Batch</th>
            <th className="py-3 pr-3">Status</th>
            <th className="py-3 pr-3">Items</th>
            <th className="py-3 pr-3 text-right">Total USD</th>
            <th className="py-3 pr-3">FX</th>
            <th className="py-3 pr-3">Created</th>
          </tr>
        </thead>
        <tbody>
          {batches.length === 0 ? <tr><td colSpan={6} className="py-8 text-center text-[var(--muted)]">No batches yet.</td></tr> : batches.map((b) => (
            <tr key={b.batch_id} className="border-b border-[rgba(15,31,23,0.05)] cursor-pointer hover:bg-white/40" onClick={() => setOpen(b.batch_id)} data-testid={`admin-batch-row-${b.batch_id}`}>
              <td className="py-3 pr-3 font-mono">{b.batch_id}</td>
              <td className="py-3 pr-3 capitalize">{b.status}</td>
              <td className="py-3 pr-3">{b.item_count}</td>
              <td className="py-3 pr-3 text-right font-mono">${(b.total_amount_cents / 100).toFixed(2)}</td>
              <td className="py-3 pr-3">{b.tzs_fx_rate ? `1 USD = ${b.tzs_fx_rate} TZS` : "—"}</td>
              <td className="py-3 pr-3 text-xs">{new Date(b.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BatchDetail({ batch_id, onBack }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [itemForm, setItemForm] = useState({});
  const refresh = async () => setData((await axios.get(`${API}/admin/payout-batch/${batch_id}`, { withCredentials: true })).data);
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [batch_id]);

  const approve = async () => { setBusy(true); try { await axios.post(`${API}/admin/payout-batch/${batch_id}/approve`, {}, { withCredentials: true }); refresh(); } finally { setBusy(false); } };
  const cancel = async () => {
    const reason = prompt("Reason for cancelling?");
    if (!reason) return;
    setBusy(true);
    try { await axios.post(`${API}/admin/payout-batch/${batch_id}/cancel`, { reason }, { withCredentials: true }); refresh(); } finally { setBusy(false); }
  };
  const markPaid = async (item_id) => {
    const f = itemForm[item_id] || {};
    if (!f.external_reference) { alert("Enter the payment reference first"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/payout-item/${item_id}/mark-paid`, {
        external_reference: f.external_reference,
        amount_tzs_cents: f.amount_tzs_cents ? parseInt(f.amount_tzs_cents, 10) : null,
        notes: f.notes || null,
      }, { withCredentials: true });
      refresh();
    } finally { setBusy(false); }
  };

  if (!data) return <Spin />;
  const b = data.batch;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-[var(--muted)] mb-4">← Back to batches</button>
      <h2 className="font-display text-2xl mb-1">{b.batch_id}</h2>
      <p className="text-[var(--muted)] mb-6">Status · <strong className="capitalize">{b.status}</strong> · Total ${(b.total_amount_cents / 100).toFixed(2)} across {b.item_count} payees {b.tzs_fx_rate ? ` · FX 1 USD = ${b.tzs_fx_rate} TZS` : ""}</p>

      <div className="flex gap-2 mb-6">
        {b.status === "draft" && <button onClick={approve} disabled={busy} className="px-5 py-2 rounded-full bg-[var(--jade)] text-white text-sm" data-testid="admin-batch-approve">Approve</button>}
        {["draft", "approved", "processing"].includes(b.status) && <button onClick={cancel} disabled={busy} className="px-5 py-2 rounded-full bg-red-600 text-white text-sm" data-testid="admin-batch-cancel">Cancel batch</button>}
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase border-b border-[rgba(15,31,23,0.08)]">
            <th className="py-3 pr-3">Affiliate</th>
            <th className="py-3 pr-3">Method</th>
            <th className="py-3 pr-3 text-right">USD</th>
            <th className="py-3 pr-3 text-right">TZS (est.)</th>
            <th className="py-3 pr-3">Status</th>
            <th className="py-3 pr-3">Pay / mark paid</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((it) => (
            <tr key={it.item_id} className="border-b border-[rgba(15,31,23,0.05)]">
              <td className="py-3 pr-3 font-mono text-xs">{it.affiliate_id}</td>
              <td className="py-3 pr-3 text-xs">{it.payout_method_snapshot?.method === "mobile_money" ? `${it.payout_method_snapshot?.mobile_provider} · ${it.payout_method_snapshot?.mobile_number}` : `${it.payout_method_snapshot?.bank_name} · ${it.payout_method_snapshot?.bank_account}`}</td>
              <td className="py-3 pr-3 text-right font-mono">${(it.amount_cents / 100).toFixed(2)}</td>
              <td className="py-3 pr-3 text-right font-mono">{it.amount_tzs_cents ? (it.amount_tzs_cents / 100).toLocaleString() : "—"}</td>
              <td className="py-3 pr-3 capitalize">{it.status}</td>
              <td className="py-3 pr-3">
                {it.status === "reserved" && b.status === "approved" ? (
                  <div className="flex gap-1 flex-wrap">
                    <input placeholder="Ref" className="input-base text-xs max-w-[120px]" onChange={(e) => setItemForm({ ...itemForm, [it.item_id]: { ...(itemForm[it.item_id] || {}), external_reference: e.target.value } })} data-testid={`admin-item-ref-${it.item_id}`} />
                    <input placeholder="TZS cents (opt)" className="input-base text-xs max-w-[120px]" onChange={(e) => setItemForm({ ...itemForm, [it.item_id]: { ...(itemForm[it.item_id] || {}), amount_tzs_cents: e.target.value } })} />
                    <button disabled={busy} onClick={() => markPaid(it.item_id)} className="px-3 py-1 rounded-full bg-[var(--ink)] text-[var(--cream)] text-xs" data-testid={`admin-item-paid-${it.item_id}`}>Mark paid</button>
                  </div>
                ) : it.status === "paid" ? (
                  <span className="text-xs text-[var(--muted)]">Ref {it.external_reference}</span>
                ) : (
                  <span className="text-xs text-[var(--muted)]">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings tab
// ---------------------------------------------------------------------------
function SettingsTab() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);

  useEffect(() => { axios.get(`${API}/admin/settings`, { withCredentials: true }).then((r) => { setS(r.data); setForm(r.data); }); }, []);

  const save = async () => {
    setBusy(true);
    try {
      const patch = {};
      ["commission_rate_bps", "attribution_window_days", "min_payout_cents", "refund_hold_days", "enrollment_fee_cents"].forEach((k) => {
        if (form[k] !== s[k]) patch[k] = parseInt(form[k], 10);
      });
      if (Object.keys(patch).length === 0) { setSavedMsg("Nothing to save."); return; }
      const r = await axios.patch(`${API}/admin/settings`, patch, { withCredentials: true });
      setS(r.data); setForm(r.data); setSavedMsg("Saved. Historical commissions keep their original rate.");
    } catch (e) {
      setSavedMsg(e?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); setTimeout(() => setSavedMsg(null), 4000); }
  };

  if (!s) return <Spin />;
  const row = (label, k, help) => (
    <div className="grid grid-cols-[1fr_200px] gap-4 items-center py-3 border-b border-[rgba(15,31,23,0.06)]">
      <div>
        <div className="font-medium">{label}</div>
        {help && <div className="text-xs text-[var(--muted)]">{help}</div>}
      </div>
      <input className="input-base text-right font-mono" type="number" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`admin-settings-${k}`} />
    </div>
  );

  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl mb-2">Program settings</h2>
      <p className="text-sm text-[var(--muted)] mb-6">Changes apply to <strong>future</strong> sales only. Each sale snapshots the active rate so historical earnings are never rewritten.</p>
      <div className="divide-y divide-[rgba(15,31,23,0.06)]">
        {row("Commission rate (bps)", "commission_rate_bps", "500 = 5.00%")}
        {row("Attribution window (days)", "attribution_window_days", "Default 30")}
        {row("Minimum payout (cents)", "min_payout_cents", "500 = $5.00")}
        {row("Refund hold (days)", "refund_hold_days", "Pending → Available after this many days")}
        {row("Enrollment fee (cents)", "enrollment_fee_cents", "299 = $2.99 — reflect this in your Paddle live price too")}
      </div>
      <button disabled={busy} onClick={save} className="mt-6 px-6 py-2.5 rounded-full bg-[var(--marigold)] text-white text-sm" data-testid="admin-settings-save">Save changes</button>
      {savedMsg && <span className="ml-4 text-sm text-[var(--muted)]">{savedMsg}</span>}
      <div className="mt-6">
        <a href={`${API}/admin/export/commissions.csv`} className="text-sm text-[var(--marigold)] underline" data-testid="admin-export-csv">Download commissions CSV</a>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit tab
// ---------------------------------------------------------------------------
function AuditTab() {
  const [data, setData] = useState(null);
  useEffect(() => { axios.get(`${API}/admin/audit?limit=200`, { withCredentials: true }).then((r) => setData(r.data)); }, []);
  if (!data) return <Spin />;
  return (
    <div>
      <h2 className="font-display text-2xl mb-4">Audit log · last 200 events</h2>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-[var(--muted)] font-mono-caption tracking-[0.2em] uppercase border-b border-[rgba(15,31,23,0.08)]">
            <th className="py-3 pr-3">When</th>
            <th className="py-3 pr-3">Actor</th>
            <th className="py-3 pr-3">Action</th>
            <th className="py-3 pr-3">Target</th>
            <th className="py-3 pr-3">Reason</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((e) => (
            <tr key={e.log_id} className="border-b border-[rgba(15,31,23,0.05)]">
              <td className="py-2 pr-3 whitespace-nowrap text-xs">{new Date(e.created_at).toLocaleString()}</td>
              <td className="py-2 pr-3 text-xs">{e.actor_type}</td>
              <td className="py-2 pr-3 font-mono text-xs">{e.action}</td>
              <td className="py-2 pr-3 text-xs">{e.target_type}/{(e.target_id || "").slice(0, 24)}</td>
              <td className="py-2 pr-3 text-xs text-[var(--muted)]">{e.reason || ""}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Metric({ label, value, tone, capitalise }) {
  const tones = { jade: "text-[var(--jade)]", marigold: "text-[var(--marigold)]" };
  return (
    <div className="p-4 rounded-2xl bg-white/70 border border-[rgba(15,31,23,0.08)]">
      <div className={`text-xs font-mono-caption tracking-[0.2em] uppercase text-[var(--muted)] ${capitalise ? "capitalize" : ""}`}>{label}</div>
      <div className={`font-display text-2xl md:text-3xl mt-1 ${tones[tone] || ""}`}>{value}</div>
    </div>
  );
}

function Spin() { return <div className="py-12 flex justify-center"><div className="w-8 h-8 rounded-full border-4 border-[var(--marigold)] border-t-transparent animate-spin" /></div>; }
